import React, { useState, useEffect } from 'react';
import { FiLayers, FiHome, FiUsers, FiRefreshCw, FiArrowLeft } from 'react-icons/fi';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import BookingModals from './bookingModel';

// Date formatting function
const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const options = { day: 'numeric', month: 'short', year: 'numeric' };
  return date.toLocaleDateString('en-US', options);
};

const BookingFloors = ({
  viewMode,
  setViewMode
}) => {
  const [floors, setFloors] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFloor, setSelectedFloor] = useState(null);
  // const [viewMode, setViewMode] = useState('floors'); // 'floors' or 'rooms'
  const [dateFilter, setDateFilter] = useState({
    checkDate: new Date().toLocaleDateString('en-CA') // Default to today's date
  });
  const [tempDateFilter, setTempDateFilter] = useState({
    checkDate: new Date().toISOString().split('T')[0] // Default to today's date
  });

  // Modal states for room interactions
  const [showCheckinModal, setShowCheckinModal] = useState(false);
  const [showBookingInfoModal, setShowBookingInfoModal] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [selectedBooking, setSelectedBooking] = useState(null);

  const yesterday = new Date(dateFilter.checkDate);
  yesterday.setDate(yesterday.getDate() + 1);

  const formattedYesterday = yesterday.toLocaleDateString('en-CA');

  // Form data for new check-in
  const [formData, setFormData] = useState({
    roomId: '',
    checkinDate: '',
    checkinTime: '',
    expectedCheckoutDate: formattedYesterday,
    expectedCheckoutTime: '11:00',
    totalAmount: '',
    paidAmount: '',
    roomAmount: '',
    remainingAmount: '',
    paymentStatus: 'pending',
    paymentMethod: 'cash',
    specialRequests: '',
    notes: '',
    persons: [{
      name: '',
      mobileNumber: '',
      identityNumber: '',
      email: '',
      address: '',
      age: '',
      gender: 'male'
    }]
  });

  // Additional form states
  const [filteredRooms, setFilteredRooms] = useState([]);
  const [selectedFloorForForm, setSelectedFloorForForm] = useState('');

  useEffect(() => {
    fetchData();
  }, []); // Only fetch on initial load

  const handleDateSubmit = () => {
    fetchData(); // Fetch data with new date
  };

  const handleDateChange = (e) => {
    // setTempDateFilter({ checkDate: e.target.value });
    setDateFilter({checkDate: e.target.value})
  };

  const handleClearDate = () => {
    const clearedFilter = { checkDate: '' };
    // setTempDateFilter(clearedFilter);
    setDateFilter(clearedFilter);
    fetchData(); // Fetch data with cleared date
  };

  const fetchData = async () => {
    try {
      setLoading(true);

      // Fetch floors with room counts for the selected date
      const params = {};
      if (dateFilter.checkDate) {
        params.checkDate = dateFilter.checkDate;
      }

      const floorsResponse = await api.get('/vendor/room-management/floors', { params });


      setFloors(floorsResponse.data);
      setRooms([]); // Clear rooms since we're not fetching them separately

    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleFloorClick = async (floor) => {
    setSelectedFloor(floor);
    setViewMode('rooms');

    // Fetch rooms for this floor
    try {
      const params = { floorId: floor._id };
      if (dateFilter.checkDate) {
        params.checkDate = dateFilter.checkDate;
      }

      const roomsResponse = await api.get('/vendor/room-management/rooms', { params });

      const roomsData = Array.isArray(roomsResponse.data.rooms)
        ? roomsResponse.data.rooms
        : Array.isArray(roomsResponse.data)
          ? roomsResponse.data
          : [];

      // Fetch booking details for booked and occupied rooms
      const roomsWithBookings = await Promise.all(
        roomsData.map(async (room) => {
          if (room.availabilityStatus === 'booked' || room.availabilityStatus === 'checked_in') {
            try {
              const status = room.availabilityStatus === 'booked' ? 'booked' : 'checked_in';
              let apiUrl = `/vendor/checkin-management/checkins?roomId=${room._id}&status=${status}`;
              
              if (dateFilter.checkDate) {
                apiUrl += `&startDate=${dateFilter.checkDate}`;
              }
              
              const bookingResponse = await api.get(apiUrl);
              const bookings = bookingResponse.data.checkins || bookingResponse.data;
              
              if (bookings.length > 0) {
                const booking = bookings[0];
                const userName = booking.persons && booking.persons.length > 0 
                  ? booking.persons[0].name 
                  : 'Guest';
                
                return {
                  ...room,
                  bookingInfo: booking,
                  userName: userName
                };
              }
            } catch (error) {
              console.error('Error fetching booking for room:', room._id, error);
            }
          }
          return room;
        })
      );

      setRooms(roomsWithBookings);
      console.log('Fetched rooms for floor with bookings:', roomsWithBookings);
    } catch (error) {
      console.error('Error fetching rooms:', error);
      toast.error('Failed to fetch rooms');
      setRooms([]);
    }
  };


  const getRoomStatusColor = (availabilityStatus) => {
    switch (availabilityStatus) {
      case 'available':
        return 'bg-green-100 border-green-300 text-green-800';
      case 'booked':
        return 'bg-blue-100 border-blue-300 text-blue-800';
      case 'checked_in':
        return 'bg-red-100 border-red-300 text-red-800';
      default:
        return 'bg-gray-100 border-gray-300 text-gray-800';
    }
  };

  const getRoomStatusDot = (availabilityStatus) => {
    switch (availabilityStatus) {
      case 'available':
        return 'bg-green-500';
      case 'booked':
        return 'bg-blue-500';
      case 'checked_in':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getRoomStatusText = (availabilityStatus) => {
    switch (availabilityStatus) {
      case 'available':
        return 'Available';
      case 'booked':
        return 'Booked';
      case 'checked_in':
        return 'Occupied';
      default:
        return 'Unknown';
    }
  };

  const getFloorStats = (floorId) => {
    // Find the floor data
    const floor = floors.find(f => f._id === floorId);

    if (!floor || !floor.roomCounts) {
      return {
        total: 0,
        available: 0,
        booked: 0,
        occupied: 0,
        maintenance: 0,
        out_of_order: 0
      };
    }


    return floor.roomCounts;
  };

  const handleRoomClick = async (room) => {
    const availabilityStatus = room.availabilityStatus || 'available';
    
    if (availabilityStatus === 'available') {
      // Open new booking modal for available rooms
      setSelectedRoom(room);
      // Pre-fill form with room and floor data
      setFormData(prev => ({ 
        ...prev, 
        roomId: room._id,
        checkinDate: dateFilter.checkDate, // Current date
        checkinTime: new Date().toTimeString().slice(0, 5) // Current time
      }));
      
      // Set the floor for the form
      setSelectedFloorForForm(room.floorId?._id || '');
      
      // Filter rooms for the selected floor
      if (room.floorId?._id) {
        const filteredRooms = rooms.filter(roomItem => 
          roomItem.floorId?._id === room.floorId?._id
        );
        setFilteredRooms(filteredRooms);
      }
      
      setShowCheckinModal(true);
    } else if (availabilityStatus === 'booked') {
      // Fetch booking details and show booking info modal
      try {
        // Get the selected date from the date picker
        const selectedDate = document.getElementById('datePicker')?.value;
        let apiUrl = `/vendor/checkin-management/checkins?roomId=${room._id}&status=booked`;
        
        // Add date filter if a date is selected
        if (selectedDate) {
          apiUrl += `&startDate=${selectedDate}`;
        }
        
        const response = await api.get(apiUrl);
        const bookings = response.data.checkins || response.data;
        
        if (bookings.length > 0) {
          setSelectedBooking(bookings[0]); // Get the first (and usually only) booking
          setShowBookingInfoModal(true);
        } else {
          toast.error('No booking found for this room on selected date');
        }
      } catch (error) {
        console.error('Error fetching booking details:', error);
        toast.error('Failed to fetch booking details');
      }
    } else if (availabilityStatus === 'occupied' || availabilityStatus === 'checked_in') {
      // Show info for occupied rooms (both 'occupied' and 'checked_in' status)
      try {
        const response = await api.get(`/vendor/checkin-management/checkins?roomId=${room._id}&status=checked_in`);
        const bookings = response.data.checkins || response.data;
        
        if (bookings.length > 0) {
          setSelectedBooking(bookings[0]);
          setShowBookingInfoModal(true);
        } else {
          toast.error('No check-in found for this room');
        }
      } catch (error) {
        console.error('Error fetching check-in details:', error);
        toast.error('Failed to fetch check-in details');
      }
    }
  };

  // Form handler functions
  const handleDirectCheckin = async (e) => {
    e.preventDefault();
    try {
      // Combine date and time for datetime fields
      const checkinDateTime = `${formData.checkinDate}T${formData.checkinTime}:00`;
      const expectedCheckoutDateTime = `${formData.expectedCheckoutDate}T${formData.expectedCheckoutTime}:00`;
      
      // Create FormData for file upload
      const formDataToSubmit = new FormData();
      
      // Add all form fields
      formDataToSubmit.append('roomId', formData.roomId);
      formDataToSubmit.append('checkinDateTime', checkinDateTime);
      formDataToSubmit.append('expectedCheckoutDateTime', new Date(expectedCheckoutDateTime).toISOString());
      formDataToSubmit.append('totalAmount', parseFloat(formData.totalAmount || 0));
      formDataToSubmit.append('paidAmount', parseFloat(formData.paidAmount || 0));
      formDataToSubmit.append('roomAmount', parseFloat(formData.roomAmount || 0));
      formDataToSubmit.append('remainingAmount', parseFloat(formData.remainingAmount || 0));
      formDataToSubmit.append('paymentStatus', formData.paymentStatus);
      formDataToSubmit.append('paymentMethod', formData.paymentMethod);
      formDataToSubmit.append('specialRequests', formData.specialRequests);
      formDataToSubmit.append('notes', formData.notes);
      formDataToSubmit.append('status', 'checked_in'); // Set status to checked_in for direct check-in
      
      // Add persons data as JSON string
      formDataToSubmit.append('persons', JSON.stringify(formData.persons));
      
      // Add Aadhar card images if they exist
      const aadharImages = [];
      formData.persons.forEach((person, index) => {
        const fileInput = document.getElementById(`aadharImage${index}`);
        if (fileInput && fileInput.files && fileInput.files[0]) {
          aadharImages.push(fileInput.files[0]);
        }
      });
      
      // Add all Aadhar images as a single array field
      aadharImages.forEach((image, index) => {
        formDataToSubmit.append(`aadharImages`, image);
      });

      const response = await api.post('/vendor/checkin-management/checkin', formDataToSubmit, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data) {
        toast.success(response.data.message);
        setShowCheckinModal(false);
        resetForm();
        fetchData(); // Refresh the data
        // Redirect to Floor Booking Overview
        setViewMode('floors');
        setSelectedFloor(null);
      }
    } catch (error) {
      console.error('Error during direct check-in:', error);
      toast.error(error.response?.data?.message || 'Failed to check in guest');
    }
  };

  const handleCheckin = async (e) => {
    e.preventDefault();
    try {
      // Combine date and time for datetime fields
      const checkinDateTime = `${formData.checkinDate}T${formData.checkinTime}:00`;
      const expectedCheckoutDateTime = `${formData.expectedCheckoutDate}T${formData.expectedCheckoutTime}:00`;
      
      // Create FormData for file upload
      const formDataToSubmit = new FormData();
      
      // Add all form fields
      formDataToSubmit.append('roomId', formData.roomId);
      formDataToSubmit.append('checkinDateTime', checkinDateTime);
      formDataToSubmit.append('expectedCheckoutDateTime', new Date(expectedCheckoutDateTime).toISOString());
      formDataToSubmit.append('totalAmount', parseFloat(formData.totalAmount));
      formDataToSubmit.append('paidAmount', parseFloat(formData.paidAmount || 0));
      formDataToSubmit.append('roomAmount', parseFloat(formData.roomAmount || 0));
      formDataToSubmit.append('remainingAmount', parseFloat(formData.remainingAmount || 0));
      formDataToSubmit.append('paymentStatus', formData.paymentStatus);
      formDataToSubmit.append('paymentMethod', formData.paymentMethod);
      formDataToSubmit.append('specialRequests', formData.specialRequests);
      formDataToSubmit.append('notes', formData.notes);
      
      // Add persons data as JSON string
      formDataToSubmit.append('persons', JSON.stringify(formData.persons));
      
      // Add Aadhar card images if they exist
      const aadharImages = [];
      formData.persons.forEach((person, index) => {
        const fileInput = document.getElementById(`aadharImage${index}`);
        if (fileInput && fileInput.files && fileInput.files[0]) {
          aadharImages.push(fileInput.files[0]);
        }
      });
      
      // Add all Aadhar images as a single array field
      aadharImages.forEach((image, index) => {
        formDataToSubmit.append(`aadharImages`, image);
      });

      const response = await api.post('/vendor/checkin-management/checkin', formDataToSubmit, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data) {
        toast.success(response.data.message);
        setShowCheckinModal(false);
        resetForm();
        fetchData(); // Refresh the data
        // Redirect to Floor Booking Overview
        setViewMode('floors');
        setSelectedFloor(null);
      }
    } catch (error) {
      console.error('Error during check-in:', error);
      toast.error(error.response?.data?.message || 'Failed to check in guest');
    }
  };

  const handleCancelBooking = async (booking, cancelData) => {
    try {
      const response = await api.post(`/vendor/checkin-management/checkins/${booking.checkinId}/cancel`, {
        remark: cancelData.remark || 'Cancelled by staff',
        refundableAmount: cancelData.refundableAmount || 0
      });

      if (response.data) {
        toast.success('Booking cancelled successfully!');
        setShowBookingInfoModal(false);
        setSelectedBooking(null);
        fetchData(); // Refresh the data
        // Redirect to Floor Booking Overview
        setViewMode('floors');
        setSelectedFloor(null);
      }
    } catch (error) {
      console.error('Error cancelling booking:', error);
      toast.error(error.response?.data?.message || 'Failed to cancel booking');
    }
  };

  const handlePerformCheckin = async (booking) => {
    try {
      // Use checkinId (custom ID) instead of _id (MongoDB ID)
      const response = await api.put(`/vendor/checkin-management/checkins/${booking.checkinId}`, {
        status: 'checked_in',
        actualCheckinDateTime: new Date()
      });
      if (response.data) {
        toast.success(response.data.message);
        setShowBookingInfoModal(false);
        setSelectedBooking(null);
        fetchData(); // Refresh the data
      }
    } catch (error) {
      console.error('Error checking in guest:', error);
      toast.error('Failed to check in guest');
    }
  };

  const handlePerformCheckout = async (booking) => {
    try {
      // Use checkinId (custom ID) instead of _id (MongoDB ID)
      const response = await api.put(`/vendor/checkin-management/checkins/${booking.checkinId}`, {
        status: 'checked_out',
        actualCheckoutDateTime: new Date()
      });
      if (response.data) {
        toast.success('Guest checked out successfully!');
        setShowBookingInfoModal(false);
        setSelectedBooking(null);
        fetchData(); // Refresh the data
      }
    } catch (error) {
      console.error('Error checking out guest:', error);
      toast.error('Failed to check out guest');
    }
  };

  const resetForm = () => {
    const yesterday = new Date(dateFilter.checkDate);
    yesterday.setDate(yesterday.getDate() + 1);

    const formattedYesterday = yesterday.toLocaleDateString('en-CA');
    setFormData({
      roomId: '',
      checkinDate: '',
      checkinTime: '',
      expectedCheckoutDate: formattedYesterday,
      expectedCheckoutTime: '11:00',
      totalAmount: '',
      paidAmount: '',
      roomAmount: '',
      remainingAmount: '',
      paymentStatus: 'pending',
      specialRequests: '',
      notes: '',
      persons: [{
        name: '',
        mobileNumber: '',
        identityNumber: '',
        email: '',
        address: '',
        age: '',
        gender: 'male'
      }]
    });
    setSelectedFloorForForm('');
    setFilteredRooms([]);
  };

  const addPerson = () => {
    const newGuestIndex = formData.persons.length;
    setFormData({
      ...formData,
      persons: [...formData.persons, { name: '', mobileNumber: '', identityNumber: '', email: '', address: '', age: '', gender: 'male' }]
    });
    
    // Scroll to the newly added guest form after a short delay to ensure DOM is updated
    setTimeout(() => {
      const guestElements = document.querySelectorAll('[data-guest-index]');
      const newGuestElement = guestElements[newGuestIndex];
      if (newGuestElement) {
        newGuestElement.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
      }
    }, 100);
  };

  const removePerson = (index) => {
    if (formData.persons.length > 1) {
      setFormData({
        ...formData,
        persons: formData.persons.filter((_, i) => i !== index)
      });
    }
  };

  const updatePerson = (index, field, value) => {
    const updatedPersons = [...formData.persons];
    updatedPersons[index] = { ...updatedPersons[index], [field]: value };
    setFormData({ ...formData, persons: updatedPersons });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Show Rooms View
  if (viewMode === 'rooms' && selectedFloor) {
    const floorRooms = rooms.filter(room => room.floorId?._id === selectedFloor._id);
    const floorStats = getFloorStats(selectedFloor._id);

    return (
      <>
        <div>
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center">
            <div>
              <h2 className="text-xl font-semibold text-gray-800">{selectedFloor.name} - Room Overview</h2>
              <p className="text-gray-600">Room status details for {selectedFloor.name}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center">
              <div className="w-4 h-4 bg-green-500 rounded mr-2"></div>
              <span className="text-sm text-gray-700">Available</span>
            </div>
            <div className="flex items-center">
              <div className="w-4 h-4 bg-blue-500 rounded mr-2"></div>
              <span className="text-sm text-gray-700">Booked</span>
            </div>
            <div className="flex items-center">
              <div className="w-4 h-4 bg-red-500 rounded mr-2"></div>
              <span className="text-sm text-gray-700">Occupied</span>
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 bg-blue-100 rounded-lg">
                <FiHome className="text-blue-600 text-xl" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-500">Total Rooms</p>
                <p className="text-2xl font-bold text-gray-800">{floorStats.total}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 bg-green-100 rounded-lg">
                <FiHome className="text-green-600 text-xl" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-500">Available</p>
                <p className="text-2xl font-bold text-green-600">{floorStats.available}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 bg-blue-100 rounded-lg">
                <FiHome className="text-blue-600 text-xl" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-500">Booked</p>
                <p className="text-2xl font-bold text-blue-600">{floorStats.booked}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 bg-red-100 rounded-lg">
                <FiUsers className="text-red-600 text-xl" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-500">Occupied</p>
                <p className="text-2xl font-bold text-red-600">{floorStats.occupied}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Room Grid */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Room Grid - {selectedFloor.name}</h3>
          </div>
          <div className="p-6">
            {floorRooms.length === 0 ? (
              <div className="text-center py-12">
                <FiHome className="mx-auto text-4xl text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No rooms found</h3>
                <p className="text-gray-500">No rooms available for this floor</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-4">
                {floorRooms.map((room) => {
                  const availabilityStatus = room.availabilityStatus || 'available';
                  return (
                    <div
                      key={room._id}
                      className={`relative group cursor-pointer transform transition-all duration-200 hover:scale-105`}
                      onClick={() => handleRoomClick(room)}
                    >
                      {/* Room Card */}
                      <div className={`relative h-32 rounded-lg shadow-md overflow-hidden border-2 ${getRoomStatusColor(availabilityStatus)}`}>
                        {/* Status Dot */}
                        <div className="absolute top-2 right-2">
                          <div className={`w-3 h-3 rounded-full ${getRoomStatusDot(availabilityStatus)}`}></div>
                        </div>

                        {/* Room Number */}
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="text-center">
                            <div className="font-bold text-lg">{room.roomNumber}</div>
                            <div className="text-xs">{room.type}</div>
                            <div className="text-xs">{room.name}</div>
                            {(availabilityStatus === 'booked' || availabilityStatus === 'checked_in') && room.checkinInfo && (
                              <div className="text-xs font-medium text-blue-700 mt-1 truncate px-1">
                                {room?.checkinInfo?.guestNames}
                              </div>
                            )}
                            {
                              (room.checkinInfo?.specialRequests || room.checkinInfo?.specialRequestsnotes) &&
                                <span className="inline-flex items-center px-2 py-1 bg-purple-100 text-purple-800 text-xs font-medium rounded-full">
                                  Special Requests
                                </span>
                            }
                          </div>
                        </div>
                      </div>

                      {/* Room Details Tooltip */}
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                        <div className="text-center">
                          <p className="font-semibold">{room.roomNumber}</p>
                          <p className="font-semibold">{room.name}</p>
                          <p>{room.type} • {room.capacity} guests</p>
                          <p className="mt-1 font-medium">{getRoomStatusText(availabilityStatus)}</p>
                          {(availabilityStatus === 'booked' || availabilityStatus === 'checked_in') && room.userName && (
                            <p className="text-xs font-bold text-blue-300 mt-1">
                              Guest: <span className="text-blue-200">{room.userName}</span>
                            </p>
                          )}
                          {dateFilter.checkDate && (
                            <p className="text-xs font-bold text-blue-300 mt-2">
                              For <span className="text-blue-200">{formatDate(dateFilter.checkDate)}</span>
                            </p>
                          )}
                        </div>
                        {/* Arrow */}
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
                          <div className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Booking Modals */}
        <BookingModals
          showCheckinModal={showCheckinModal}
          setShowCheckinModal={setShowCheckinModal}
          showBookingInfoModal={showBookingInfoModal}
          setShowBookingInfoModal={setShowBookingInfoModal}
          selectedRoom={selectedRoom}
          selectedBooking={selectedBooking}
          setSelectedBooking={setSelectedBooking}
          // Form related props
          floors={floors}
          rooms={rooms}
          filteredRooms={filteredRooms}
          setFilteredRooms={setFilteredRooms}
          selectedFloor={selectedFloorForForm}
          setSelectedFloor={setSelectedFloorForForm}
          formData={formData}
          setFormData={setFormData}
          handleCheckin={handleCheckin}
          handleDirectCheckin={handleDirectCheckin}
          handleCancelBooking={handleCancelBooking}
          resetForm={resetForm}
          addPerson={addPerson}
          removePerson={removePerson}
          updatePerson={updatePerson}
          handlePerformCheckin={handlePerformCheckin}
          handlePerformCheckout={handlePerformCheckout}
          fetchData={fetchData}
          setViewMode={setViewMode}
        />
      </>
    );
  }

  // Show Floors View
  if (viewMode === 'floors') {
    return (
      <>
        <div>
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-xl font-semibold text-gray-800">Floor Booking Overview</h2>
              <p className="text-gray-600">Check room availability across floors</p>
            </div>
            <button
              onClick={fetchData}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center hover:bg-blue-700 transition-colors"
            >
              <FiRefreshCw className="mr-2" />
              Refresh
            </button>
          </div>

          {/* Date Filter */}
          <div className="bg-white rounded-lg shadow mb-6">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Check Availability Date</h3>
            </div>
            <div className="px-6 py-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Check Availability Date</label>
                  <input
                    type="date"
                    value={dateFilter.checkDate}
                    onChange={handleDateChange}
                    min={new Date().toISOString().split('T')[0]} // Disable previous dates
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="flex items-end space-x-2">
                  <button
                    onClick={handleDateSubmit}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Submit
                  </button>
                  <button
                    onClick={handleClearDate}
                    className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    Clear Date
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Floor List */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200 sticky top-0 bg-white z-10">
              <h3 className="text-lg font-semibold text-gray-900">Select Floor</h3>
              <div className="flex flex-wrap gap-6 mt-3">
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-green-500 rounded mr-2"></div>
                  <span className="text-sm text-gray-700">Available</span>
                </div>
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-blue-500 rounded mr-2"></div>
                  <span className="text-sm text-gray-700">Booked</span>
                </div>
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-red-500 rounded mr-2"></div>
                  <span className="text-sm text-gray-700">Occupied</span>
                </div>
              </div>
            </div>
            <div className="p-6">
              {floors.length === 0 ? (
                <div className="text-center py-12">
                  <FiLayers className="mx-auto text-4xl text-gray-400 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No floors found</h3>
                  <p className="text-gray-500">Get started by creating your first floor</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {floors.map((floor) => {
                    const stats = getFloorStats(floor._id);
                    const availableCount = stats.available; // Use stats directly from API
                    return (
                      <div
                        key={floor._id}
                        onClick={() => handleFloorClick(floor)}
                        className="border border-gray-200 rounded-lg p-6 hover:bg-gray-50 cursor-pointer transition-all duration-200 hover:shadow-lg"
                      >
                        {/* Floor Header */}
                        <div className="flex items-center justify-between mb-4">
                          <div className="p-3 bg-gray-100 rounded-lg">
                            <FiLayers className="text-gray-600 text-xl" />
                          </div>
                          <FiArrowLeft className="text-gray-400 transform rotate-180" />
                        </div>

                        {/* Floor Info */}
                        <div className="mb-4">
                          <h4 className="text-lg font-semibold text-gray-900">{floor.name}</h4>
                          <p className="text-sm text-gray-600">{stats.total} Rooms</p>
                        </div>

                        {/* Available Rooms - Prominent */}
                        <div className="flex items-center justify-center p-3 bg-green-50 rounded-lg mb-3">
                          <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                          <span className="text-lg font-bold text-green-700">
                            {availableCount} Available
                          </span>
                        </div>

                        {/* Other Room Counts */}
                        <div className="flex justify-between text-sm">
                          <span className="flex items-center text-gray-600">
                            <div className="w-2 h-2 bg-blue-500 rounded-full mr-1"></div>
                            {stats.booked} Booked
                          </span>
                          <span className="flex items-center text-gray-600">
                            <div className="w-2 h-2 bg-red-500 rounded-full mr-1"></div>
                            {stats.occupied} Occupied
                          </span>
                        </div>

                        {/* Date-specific availability message */}
                        {dateFilter.checkDate && (
                          <div className="mt-3 pt-3 border-t border-gray-200">
                            <p className="text-sm font-bold text-blue-600 text-center bg-blue-50 rounded-lg py-2">
                              Available on <span className="text-blue-700">{formatDate(dateFilter.checkDate)}</span>
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </>
    );
  }
};

export default BookingFloors;
